import { useMemo, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout } from 'antd';
import clsx from 'clsx';
import { useAppSelector } from 'app/hooks';
import useIsMobile from 'hooks/useIsMobile';
import If from 'components/If';
import { getFromBrandConfig } from 'brand-config';
import { selectUserSignInStatus } from 'services/session/sessionSelectors';
import { QaLoginPage } from 'testdata/constants';

// @Styles
import { SERVICES } from 'utils/service-entitlements-config';
import useIsFeatureAvailable from 'hooks/useIsFeatureAvailable';
import styles from './Footer.module.scss';

const HIDE_FOOTER_LINKS = new Set([
  '/terms/service-terms',
  '/policy/privacy-policy',
  '/policy/id-theft',
]);

function Footer() {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const isLogin = useAppSelector(selectUserSignInStatus);
  const hasInsurance = useIsFeatureAvailable(SERVICES.insurance);
  const currentYear = new Date().getFullYear();
  const copyRightNotice = `© ${currentYear} ${getFromBrandConfig('copyRightEntity')}. All rights reserved.`;

  const footerClassNames = clsx(styles.footer, {
    [styles.fixedFooter]: !isMobile,
    [styles.logedInFooter]: isLogin,
  });

  const shouldHideLinks = useMemo(() => {
    return HIDE_FOOTER_LINKS.has(pathname);
  }, [pathname]);

  const showFaqLink = useMemo(() => {
    return pathname.includes('enrollment');
  }, [pathname]);

  return (
    <Layout.Footer
      className={footerClassNames}
      data-qa-selector={QaLoginPage.textFooter}
    >
      <ul className={styles.serviceTermsLinks}>
        <If condition={!shouldHideLinks}>
          <li>
            <a
              href='/terms-of-service'
              target='_blank'
              rel='noreferrer'
              className={styles.link}
              data-qa-selector={QaLoginPage.linkTermsOfService}
            >
              Terms of Service
            </a>
          </li>
          <li>
            <a
              href='/privacy-policy'
              target='_blank'
              rel='noreferrer'
              className={styles.link}
              data-qa-selector={QaLoginPage.linkPrivacyPolicy}
            >
              Policies
            </a>
          </li>
        </If>
        <If condition={isLogin && hasInsurance}>
          <li>
            <Link className={styles.link} to='/insurance'>
              Insurance
            </Link>
          </li>
        </If>
        <If condition={!isLogin && showFaqLink}>
          <li>
            <Link className={styles.link} to='/faq'>
              FAQs
            </Link>
          </li>
        </If>
      </ul>
      <ul className={styles.copyrightText}>
        <li>{copyRightNotice}</li>
        <If condition={!!getFromBrandConfig('showTheftText')}>
          <li>No one can prevent all identity theft.</li>
        </If>
      </ul>
    </Layout.Footer>
  );
}

export default memo(Footer);
